import { Group, Paper, SegmentedControl, Stack, Text } from "@mantine/core";
import { AssistantOrb } from "@/components/assistant/orb";
import {
	ORB_CANVAS_FAB_PX,
	ORB_CANVAS_HEADER_PX,
} from "@/components/assistant/orb/orb.config";
import type { AssistantOrbText } from "@/locales/assistant-orb";
import type { AssistantOrbConfig } from "@/types/ai-assistant-orb";

/**
 * Pratinjau besar + kedua ukuran yang benar-benar dipakai produk.
 *
 * Baris ukuran asli memakai pemetaan yang sama persis dengan FAB & header,
 * sehingga memilih ukuran di admin langsung terlihat sebagaimana nanti di
 * produk — hanya kanvas orb yang membesar.
 *
 * Beda dengan produk: di sini slot SENGAJA dipesan sebesar kanvas terbesar,
 * bukan footprint asli. Di FAB/header asli orb boleh meluber ke area kosong,
 * tapi di pratinjau ada label dan nama tepat di sebelahnya, jadi meluber =
 * menimpa. Sebesar kanvas TERBESAR (bukan kanvas ukuran terpilih) supaya
 * tata letak tidak melompat tiap admin berganti ukuran.
 */

/** Slot pratinjau tombol bulat: cukup untuk kanvas FAB terbesar. */
export const PREVIEW_FAB_SLOT_PX = Math.max(
	...Object.values(ORB_CANVAS_FAB_PX),
);
/** Slot pratinjau header: cukup untuk kanvas header terbesar. */
export const PREVIEW_HEADER_SLOT_PX = Math.max(
	...Object.values(ORB_CANVAS_HEADER_PX),
);

/** Sisi pratinjau besar (px). */
const PREVIEW_PX = 160;

export function OrbPreview({
	config,
	name,
	text,
	light,
	onThemeChange,
}: {
	config: AssistantOrbConfig;
	name: string;
	text: AssistantOrbText;
	light: boolean;
	onThemeChange(light: boolean): void;
}) {
	return (
		<Stack gap="sm" data-orb-preview>
			<Paper
				withBorder
				radius="md"
				p="lg"
				style={{
					display: "grid",
					placeItems: "center",
					// Latar pratinjau mengikuti tema yang sedang dipilih, bukan tema
					// dashboard — kalau tidak, orb terang diuji di atas latar gelap.
					background: light
						? "var(--mantine-color-gray-0)"
						: "var(--mantine-color-dark-8)",
				}}
			>
				<AssistantOrb
					config={config}
					name={name}
					size={PREVIEW_PX}
					forceLight={light}
				/>
			</Paper>

			<SegmentedControl
				size="xs"
				value={light ? "light" : "dark"}
				onChange={(value) => onThemeChange(value === "light")}
				data-orb-theme-toggle
				data={[
					{ value: "dark", label: text.previewDark },
					{ value: "light", label: text.previewLight },
				]}
			/>

			<Group gap="xl" align="flex-end" data-orb-real-sizes>
				<Stack gap={4} align="center">
					{/* Slot dipesan sebesar kanvas terbesar; cakram orb dipusatkan di dalamnya. */}
					<div
						style={{
							width: PREVIEW_FAB_SLOT_PX,
							height: PREVIEW_FAB_SLOT_PX,
							display: "grid",
							placeItems: "center",
						}}
						data-orb-preview-fab
					>
						<div
							style={{
								width: ORB_CANVAS_FAB_PX[config.size],
								height: ORB_CANVAS_FAB_PX[config.size],
								borderRadius: "50%",
								overflow: "hidden",
								display: "grid",
								placeItems: "center",
							}}
						>
							<AssistantOrb
								config={config}
								name={name}
								size={ORB_CANVAS_FAB_PX[config.size]}
								forceLight={light}
							/>
						</div>
					</div>
					<Text size="xs" c="dimmed">
						{text.previewFab}
					</Text>
				</Stack>

				<Stack gap={4}>
					<Paper withBorder radius="md" px="md" py="sm" data-orb-preview-header>
						<Group gap="xs" wrap="nowrap">
							<span
								data-orb-preview-header-slot
								style={{
									width: PREVIEW_HEADER_SLOT_PX,
									height: PREVIEW_HEADER_SLOT_PX,
									flexShrink: 0,
									display: "grid",
									placeItems: "center",
								}}
							>
								<AssistantOrb
									config={config}
									name={name}
									size={ORB_CANVAS_HEADER_PX[config.size]}
									forceLight={light}
								/>
							</span>
							<Text fw={700}>{name}</Text>
						</Group>
					</Paper>
					<Text size="xs" c="dimmed">
						{text.previewHeader}
					</Text>
				</Stack>
			</Group>
		</Stack>
	);
}
